// ══════════════════════════════════════════════════════════════════════════════
// PUBLISH PRO MODAL - Modal de publication pour les professionnels
// Permet de publier : Posts, Produits, Prestations, Posts Réseau Pro
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, ArrowLeft, Camera, Package, Wrench, Users, Calendar, 
  MessageSquare, FileText, Upload, Plus, Check, AlertCircle, 
  Image, Video, Zap, Target, Newspaper, Network, Store, Euro,
  Tag, Layers, MapPin, Clock, Star, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Options de publication pour les Pros
const PRO_PUBLISH_OPTIONS = [
  { 
    id: 'post', 
    icon: Camera, 
    title: 'Publication', 
    subtitle: 'Partager une réalisation, actualité',
    color: 'blue',
    description: 'Visible sur le réseau social'
  },
  { 
    id: 'post_pro', 
    icon: Network, 
    title: 'Post Réseau Pro', 
    subtitle: 'Réservé aux professionnels',
    color: 'slate',
    description: 'Visible uniquement par les pros'
  },
  { 
    id: 'produit', 
    icon: Package, 
    title: 'Nouveau Produit', 
    subtitle: 'Ajouter au catalogue Marketplace',
    color: 'orange',
    description: 'Visible sur la Marketplace'
  },
  { 
    id: 'prestation', 
    icon: Wrench, 
    title: 'Prestation', 
    subtitle: 'Activer un service artisan',
    color: 'blue',
    description: 'Visible sur l\'annuaire Artisans'
  },
  { 
    id: 'vente_flash', 
    icon: Zap, 
    title: 'Vente Flash', 
    subtitle: 'Promotion limitée dans le temps',
    color: 'orange',
    description: 'Mise en avant sur la Marketplace'
  },
  { 
    id: 'evenement', 
    icon: Calendar, 
    title: 'Événement Pro', 
    subtitle: 'Formation, salon, réunion',
    color: 'blue',
    description: 'Visible dans le calendrier pro'
  },
];

// Catégories de produits (rayons avec sous-rayons)
const RAYONS = {
  outillage: {
    id: 'outillage',
    label: 'Outillage Pro',
    emoji: '🔧',
    color: '#DC2626',
    sousRayons: [
      { id: 'electroportatif', label: 'Électroportatif' },
      { id: 'outillage_main', label: 'Outillage à main' },
      { id: 'machines', label: 'Machines stationnaires' },
      { id: 'mesure', label: 'Mesure & traçage' },
      { id: 'echafaudages', label: 'Échafaudages' },
      { id: 'echelles', label: 'Échelles & escabeaux' },
      { id: 'compresseurs', label: 'Compresseurs' },
      { id: 'soudure', label: 'Soudure' },
    ]
  },
  electricite: {
    id: 'electricite',
    label: 'Fournitures Électriques',
    emoji: '⚡',
    color: '#F59E0B',
    sousRayons: [
      { id: 'cables', label: 'Câbles & fils' },
      { id: 'tableaux', label: 'Tableaux électriques' },
      { id: 'appareillage', label: 'Appareillage' },
      { id: 'eclairage', label: 'Éclairage' },
      { id: 'domotique', label: 'Domotique' },
      { id: 'prises', label: 'Prises & interrupteurs' },
      { id: 'disjoncteurs', label: 'Disjoncteurs' },
      { id: 'bornes_recharge', label: 'Bornes de recharge' },
    ]
  },
  plomberie: {
    id: 'plomberie',
    label: 'Fournitures Plomberie',
    emoji: '🚿',
    color: '#3B82F6',
    sousRayons: [
      { id: 'tuyaux', label: 'Tuyaux & tubes' },
      { id: 'raccords', label: 'Raccords' },
      { id: 'sanitaires', label: 'Sanitaires' },
      { id: 'robinetterie', label: 'Robinetterie' },
      { id: 'chauffe_eau', label: 'Chauffe-eau' },
      { id: 'evacuation', label: 'Évacuation' },
      { id: 'pompes', label: 'Pompes' },
      { id: 'traitement_eau', label: 'Traitement eau' },
    ]
  },
  quincaillerie: {
    id: 'quincaillerie',
    label: 'Quincaillerie',
    emoji: '🔩',
    color: '#78716C',
    sousRayons: [
      { id: 'visserie', label: 'Visserie' },
      { id: 'boulonnerie', label: 'Boulonnerie' },
      { id: 'chevilles', label: 'Chevilles & fixations' },
      { id: 'serrurerie', label: 'Serrurerie' },
      { id: 'charnieres', label: 'Charnières' },
      { id: 'poignees', label: 'Poignées' },
      { id: 'ferrures', label: 'Ferrures' },
      { id: 'colles', label: 'Colles & adhésifs' },
    ]
  },
  peinture: {
    id: 'peinture',
    label: 'Peinture & Décoration',
    emoji: '🖌️',
    color: '#EC4899',
    sousRayons: [
      { id: 'peinture_int', label: 'Peinture intérieure' },
      { id: 'peinture_ext', label: 'Peinture extérieure' },
      { id: 'enduits', label: 'Enduits' },
      { id: 'papiers_peints', label: 'Papiers peints' },
      { id: 'accessoires_peinture', label: 'Accessoires peinture' },
      { id: 'lasures', label: 'Lasures & vernis' },
      { id: 'sous_couches', label: 'Sous-couches' },
      { id: 'outils_peintre', label: 'Outils de peintre' },
    ]
  },
  carrelage: {
    id: 'carrelage',
    label: 'Carrelage & Revêtements',
    emoji: '🎨',
    color: '#8B5CF6',
    sousRayons: [
      { id: 'carrelage_sol', label: 'Carrelage sol' },
      { id: 'carrelage_mural', label: 'Carrelage mural' },
      { id: 'faience', label: 'Faïence' },
      { id: 'parquet', label: 'Parquet' },
      { id: 'stratifie', label: 'Stratifié' },
      { id: 'sols_souples', label: 'Sols souples' },
      { id: 'mosaique', label: 'Mosaïque' },
      { id: 'terrasse', label: 'Terrasse extérieure' },
    ]
  },
  materiaux: {
    id: 'materiaux',
    label: 'Négoce Matériaux',
    emoji: '🏗️',
    color: '#6B7280',
    sousRayons: [
      { id: 'gros_oeuvre', label: 'Gros œuvre' },
      { id: 'second_oeuvre', label: 'Second œuvre' },
      { id: 'isolation', label: 'Isolation' },
      { id: 'bois', label: 'Bois & dérivés' },
      { id: 'plaques', label: 'Plaques & cloisons' },
      { id: 'toiture', label: 'Toiture' },
      { id: 'beton', label: 'Béton & ciment' },
      { id: 'agregats', label: 'Pierre & agrégats' },
    ]
  },
  chauffage: {
    id: 'chauffage',
    label: 'Chauffage & Climatisation',
    emoji: '🌡️',
    color: '#EF4444',
    sousRayons: [
      { id: 'radiateurs', label: 'Radiateurs' },
      { id: 'chaudieres', label: 'Chaudières' },
      { id: 'pac', label: 'Pompes à chaleur' },
      { id: 'climatiseurs', label: 'Climatiseurs' },
      { id: 'vmc', label: 'VMC' },
      { id: 'poeles', label: 'Poêles & inserts' },
      { id: 'regulation', label: 'Régulation' },
      { id: 'accessoires_chauffage', label: 'Accessoires chauffage' },
    ]
  },
  menuiserie: {
    id: 'menuiserie',
    label: 'Menuiseries',
    emoji: '🚪',
    color: '#92400E',
    sousRayons: [
      { id: 'portes_int', label: 'Portes intérieures' },
      { id: 'portes_entree', label: "Portes d'entrée" },
      { id: 'fenetres', label: 'Fenêtres' },
      { id: 'volets', label: 'Volets' },
      { id: 'portails', label: 'Portails' },
      { id: 'stores', label: 'Stores & pergolas' },
      { id: 'verrieres', label: 'Verrières' },
      { id: 'escaliers', label: 'Escaliers' },
    ]
  },
  cuisine_sdb: {
    id: 'cuisine_sdb',
    label: 'Cuisine & Salle de bain',
    emoji: '🍳',
    color: '#0EA5E9',
    sousRayons: [
      { id: 'meubles_cuisine', label: 'Meubles de cuisine' },
      { id: 'plans_travail', label: 'Plans de travail' },
      { id: 'electromenager', label: 'Électroménager' },
      { id: 'douches', label: 'Douches' },
      { id: 'baignoires', label: 'Baignoires' },
      { id: 'meubles_sdb', label: 'Meubles de salle de bain' },
      { id: 'vasques', label: 'Vasques & lavabos' },
      { id: 'accessoires_sdb', label: 'Accessoires' },
    ]
  },
  jardin: {
    id: 'jardin',
    label: 'Jardin & Extérieur',
    emoji: '🌳',
    color: '#22C55E',
    sousRayons: [
      { id: 'clotures', label: 'Clôtures & grillages' },
      { id: 'mobilier_jardin', label: 'Mobilier de jardin' },
      { id: 'terrasses', label: 'Terrasses' },
      { id: 'arrosage', label: 'Arrosage' },
      { id: 'eclairage_ext', label: 'Éclairage extérieur' },
      { id: 'abris', label: 'Abris de jardin' },
      { id: 'piscines', label: 'Piscines' },
      { id: 'outillage_jardin', label: 'Outillage jardin' },
    ]
  },
  equipements: {
    id: 'equipements',
    label: 'Équipements Pro',
    emoji: '🦺',
    color: '#059669',
    sousRayons: [
      { id: 'epi', label: 'EPI (Casques, gants...)' },
      { id: 'vetements', label: 'Vêtements de travail' },
      { id: 'chaussures', label: 'Chaussures de sécurité' },
      { id: 'signalisation', label: 'Signalisation' },
      { id: 'protection_auditive', label: 'Protection auditive' },
      { id: 'protection_respiratoire', label: 'Protection respiratoire' },
      { id: 'premiers_secours', label: 'Premiers secours' },
      { id: 'rangement', label: 'Rangement & organisation' },
    ]
  },
};

// Liste des rayons pour l'affichage
const RAYONS_LIST = Object.values(RAYONS);

// Métiers pour prestations
const METIERS = [
  { id: 'plombier', label: 'Plombier', emoji: '🔧' },
  { id: 'electricien', label: 'Électricien', emoji: '⚡' },
  { id: 'macon', label: 'Maçon', emoji: '🧱' },
  { id: 'carreleur', label: 'Carreleur', emoji: '🔲' },
  { id: 'peintre', label: 'Peintre', emoji: '🎨' },
  { id: 'menuisier', label: 'Menuisier', emoji: '🪵' },
  { id: 'couvreur', label: 'Couvreur', emoji: '🏠' },
  { id: 'chauffagiste', label: 'Chauffagiste', emoji: '🌡️' },
  { id: 'pisciniste', label: 'Pisciniste', emoji: '🏊' },
  { id: 'paysagiste', label: 'Paysagiste', emoji: '🌳' },
  { id: 'climaticien', label: 'Climaticien', emoji: '❄️' },
  { id: 'facadier', label: 'Façadier', emoji: '🏗️' },
  { id: 'serrurier', label: 'Serrurier', emoji: '🔐' },
  { id: 'vitrier', label: 'Vitrier', emoji: '🪟' },
  { id: 'charpentier', label: 'Charpentier', emoji: '🪓' },
  { id: 'jardinier', label: 'Jardinier', emoji: '🌱' },
  { id: 'architecte', label: 'Architecte', emoji: '📐' },
  { id: 'geometre', label: 'Géomètre', emoji: '📏' },
  { id: 'expert_batiment', label: 'Expert bâtiment', emoji: '🔍' },
  { id: 'bureau_etudes', label: 'Bureau d\'études', emoji: '📊' },
  { id: 'diagnostiqueur', label: 'Diagnostiqueur', emoji: '📋' },
  { id: 'maitre_oeuvre', label: 'Maître d\'œuvre', emoji: '👷' },
];

// Couleurs par type
const TYPE_COLORS = {
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-[#F97316]', hover: 'hover:border-[#F97316]' },
  blue: { bg: 'bg-[#F0F4F8]', border: 'border-[#1E3A5F]/20', icon: 'text-[#1E3A5F]', hover: 'hover:border-[#1E3A5F]' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', hover: 'hover:border-slate-400' },
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function PublishProModal({ isOpen, onClose, userProfile, subscription }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Rayons chargés depuis le catalogue
  const [catalogueRayons, setCatalogueRayons] = useState([]);
  const [newRayonName, setNewRayonName] = useState('');
  const [newSousRayonName, setNewSousRayonName] = useState('');
  const [showAddRayon, setShowAddRayon] = useState(false);
  const [showAddSousRayon, setShowAddSousRayon] = useState(false);
  
  // États formulaire Post
  const [postText, setPostText] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [isProOnly, setIsProOnly] = useState(false);
  
  // États formulaire Produit
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productStock, setProductStock] = useState('');
  const [productRayon, setProductRayon] = useState('');
  const [productSousRayon, setProductSousRayon] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productImages, setProductImages] = useState([]);
  
  // États formulaire Prestation
  const [prestationMetier, setPrestationMetier] = useState('');
  const [prestationName, setPrestationName] = useState('');
  const [prestationPrice, setPrestationPrice] = useState('');
  const [prestationZone, setPrestationZone] = useState(30);
  const [prestationDescription, setPrestationDescription] = useState('');

  const fileInputRef = useRef(null);
  const token = localStorage.getItem('client_token');

  // Charger les rayons du catalogue
  useEffect(() => {
    if (isOpen) {
      loadCatalogueRayons();
    }
  }, [isOpen]);

  const loadCatalogueRayons = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/rayons`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setCatalogueRayons(data.rayons || []);
      }
    } catch (error) {
      console.error('Error loading rayons:', error);
    }
  };

  // Ajouter un nouveau rayon
  const handleAddRayon = async () => {
    if (!newRayonName.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/rayons?name=${encodeURIComponent(newRayonName)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(`Rayon "${newRayonName}" créé !`);
          loadCatalogueRayons();
          setNewRayonName('');
          setShowAddRayon(false);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la création du rayon');
    }
  };

  // Ajouter un nouveau sous-rayon
  const handleAddSousRayon = async () => {
    if (!newSousRayonName.trim() || !productRayon) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/rayons/${productRayon}/sous-rayons?name=${encodeURIComponent(newSousRayonName)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(`Sous-rayon "${newSousRayonName}" ajouté !`);
          loadCatalogueRayons();
          setNewSousRayonName('');
          setShowAddSousRayon(false);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la création du sous-rayon');
    }
  };

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedType(null);
      setPostText('');
      setPostImages([]);
      setProductName('');
      setProductPrice('');
      setProductStock('');
      setProductRayon('');
      setProductSousRayon('');
      setProductDescription('');
      setProductImages([]);
      setPrestationMetier('');
      setPrestationName('');
      setPrestationPrice('');
      setPrestationZone(30);
      setPrestationDescription('');
      setShowAddRayon(false);
      setShowAddSousRayon(false);
    }
  }, [isOpen]);

  // Vérifier les limites d'abonnement
  const checkLimits = () => {
    const limits = {
      artisan: { products: 100, team: 1 },
      pro: { products: 1000, team: 5 },
      business: { products: 10000, team: 10 },
    };
    const currentLimit = limits[subscription] || limits.artisan;
    const currentCount = userProfile?.productCount || 0;
    
    return {
      canAddProduct: currentCount < currentLimit.products,
      remainingProducts: currentLimit.products - currentCount,
      limit: currentLimit.products,
    };
  };

  // Sélectionner un type
  const handleSelectType = (option) => {
    // Vérifier si c'est un produit et si on a atteint la limite
    if (option.id === 'produit') {
      const limits = checkLimits();
      if (!limits.canAddProduct) {
        toast.error(`Limite atteinte ! Ton abonnement permet ${limits.limit} produits max.`);
        return;
      }
    }
    
    setSelectedType(option);
    setStep(2);
  };

  // Upload d'images
  const handleImageUpload = (e, target) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limiter à 5 images
    const maxImages = 5;
    const currentImages = target === 'post' ? postImages : productImages;
    
    if (currentImages.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images`);
      return;
    }

    // Créer des previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = { file, preview: e.target.result };
        if (target === 'post') {
          setPostImages(prev => [...prev, newImage]);
        } else {
          setProductImages(prev => [...prev, newImage]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Supprimer une image
  const removeImage = (index, target) => {
    if (target === 'post') {
      setPostImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setProductImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      toast.error('Tu dois être connecté');
      return;
    }

    setIsLoading(true);

    try {
      let endpoint = '';
      let body = {};

      switch (selectedType.id) {
        case 'post':
        case 'post_pro':
          endpoint = '/api/posts';
          body = {
            content: postText,
            images: postImages.map(img => img.preview), // TODO: Upload real images
            is_pro_only: selectedType.id === 'post_pro',
            post_type: 'publication',
          };
          break;

        case 'produit':
          endpoint = '/api/cockpit/products';
          // Utiliser les rayons du catalogue ou fallback
          const rayonsForSubmit = catalogueRayons.length > 0 ? catalogueRayons : RAYONS_LIST.map(r => ({
            id: r.id,
            name: r.label,
            sous_rayons: r.sousRayons.map(sr => ({ id: sr.id, name: sr.label }))
          }));
          const selectedRayon = rayonsForSubmit.find(r => r.id === productRayon);
          const selectedSousRayon = selectedRayon?.sous_rayons?.find(sr => sr.id === productSousRayon);
          
          body = {
            name: productName,
            price_ttc: parseFloat(productPrice),
            stock: parseInt(productStock) || 0,
            rayon_id: productRayon,
            rayon_name: selectedRayon?.name || productRayon,
            sous_rayon_id: productSousRayon,
            sous_rayon_name: selectedSousRayon?.name || productSousRayon,
            description_short: productDescription.slice(0, 200),
            description_long: productDescription,
            photos: productImages.map(img => ({ url: img.preview, is_primary: true })),
            status: 'active',
          };
          break;

        case 'prestation':
          endpoint = '/api/cockpit/prestations';
          body = {
            metier: METIERS.find(m => m.id === prestationMetier)?.label || '',
            metier_slug: prestationMetier,
            name: prestationName,
            description_short: prestationDescription.slice(0, 200),
            description_long: prestationDescription,
            tarif_type: 'horaire',
            tarif_montant: parseFloat(prestationPrice) || 0,
            tarif_unite: '€/h',
            zone_intervention_km: prestationZone,
            status: 'active',
          };
          break;

        default:
          toast.info('Fonctionnalité bientôt disponible !');
          onClose();
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Publié avec succès ! 🎉');
        onClose();
        
        // Rediriger vers la page appropriée
        if (selectedType.id === 'produit') {
          navigate('/cockpit-pro/catalogue/produits');
        } else if (selectedType.id === 'prestation') {
          navigate('/cockpit-pro/catalogue/prestations');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  // Render Step 1: Choix du type
  const renderStep1 = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Que veux-tu publier ?</h2>
      <p className="text-gray-500 text-sm mb-6">Choisis le type de contenu à créer</p>
      
      <div className="grid grid-cols-2 gap-3">
        {PRO_PUBLISH_OPTIONS.map((option) => {
          const colors = TYPE_COLORS[option.color];
          const Icon = option.icon;
          const isDisabled = option.id === 'produit' && !checkLimits().canAddProduct;
          
          return (
            <button
              key={option.id}
              onClick={() => !isDisabled && handleSelectType(option)}
              disabled={isDisabled}
              className={`
                relative p-4 rounded-xl border-2 text-left transition-all
                ${colors.bg} ${colors.border} ${colors.hover}
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}
              `}
            >
              <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center mb-3`}>
                <Icon size={22} className={colors.icon} />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{option.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{option.subtitle}</p>
              
              {option.id === 'produit' && (
                <div className="mt-2 text-xs text-gray-400">
                  {checkLimits().remainingProducts} / {checkLimits().limit} restants
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Render Step 2: Formulaire selon le type
  const renderStep2 = () => {
    switch (selectedType?.id) {
      case 'post':
      case 'post_pro':
        return renderPostForm();
      case 'produit':
        return renderProductForm();
      case 'prestation':
        return renderPrestationForm();
      default:
        return (
          <div className="p-6 text-center">
            <Sparkles size={48} className="text-[#F97316] mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">Bientôt disponible !</h3>
            <p className="text-gray-500 text-sm">Cette fonctionnalité arrive très vite.</p>
          </div>
        );
    }
  };

  // Formulaire Post
  const renderPostForm = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-1">
        {selectedType?.id === 'post_pro' ? 'Post Réseau Pro' : 'Nouvelle publication'}
      </h2>
      <p className="text-gray-500 text-sm mb-4">
        {selectedType?.id === 'post_pro' 
          ? 'Ce post sera visible uniquement par les professionnels'
          : 'Partage avec toute la communauté'
        }
      </p>

      {/* Zone de texte */}
      <textarea
        value={postText}
        onChange={(e) => setPostText(e.target.value)}
        placeholder="Raconte ta réalisation, partage un conseil..."
        className="w-full h-32 p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
      />

      {/* Images */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Image size={16} className="text-gray-400" />
          <span className="text-sm text-gray-600">Photos ({postImages.length}/5)</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {postImages.map((img, index) => (
            <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
              <img src={img.preview} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(index, 'post')}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {postImages.length < 5 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-[#1E3A5F] transition-colors"
            >
              <Plus size={24} className="text-gray-400" />
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleImageUpload(e, 'post')}
          className="hidden"
        />
      </div>

      {/* Bouton publier */}
      <button
        onClick={handleSubmit}
        disabled={!postText.trim() || isLoading}
        className="mt-6 w-full py-3 bg-[#F97316] text-white font-semibold rounded-xl hover:bg-[#ea6c0a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Check size={18} />
            Publier
          </>
        )}
      </button>
    </div>
  );

  // Formulaire Produit
  const renderProductForm = () => {
    // Utiliser les rayons du catalogue ou fallback sur RAYONS hardcodés
    const rayonsToUse = catalogueRayons.length > 0 ? catalogueRayons : RAYONS_LIST.map(r => ({
      id: r.id,
      name: r.label,
      sous_rayons: r.sousRayons.map(sr => ({ id: sr.id, name: sr.label }))
    }));
    
    const selectedRayonData = productRayon 
      ? rayonsToUse.find(r => r.id === productRayon) 
      : null;
    
    // Trouver les données de style depuis RAYONS (pour les couleurs/emoji)
    const rayonStyle = RAYONS[productRayon] || { emoji: '📦', color: '#6B7280' };
    
    return (
    <div className="p-6 max-h-[70vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Nouveau produit</h2>
      <p className="text-gray-500 text-sm mb-4">Ce produit sera visible sur la Marketplace</p>

      <div className="space-y-4">
        {/* Nom */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit *</label>
          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Ex: Perceuse visseuse 18V"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
          />
        </div>

        {/* Rayon */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Rayon * <span className="text-gray-400 font-normal">({rayonsToUse.length} rayons dans le catalogue)</span>
            </label>
            <button
              type="button"
              onClick={() => setShowAddRayon(!showAddRayon)}
              className="text-xs text-[#F97316] hover:underline flex items-center gap-1"
            >
              <Plus size={12} />
              Nouveau rayon
            </button>
          </div>
          
          {/* Formulaire ajout rayon */}
          {showAddRayon && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-3 p-3 bg-orange-50 rounded-xl border border-orange-200"
            >
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newRayonName}
                  onChange={(e) => setNewRayonName(e.target.value)}
                  placeholder="Nom du nouveau rayon"
                  className="flex-1 px-3 py-2 text-sm border border-orange-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F97316]/20"
                />
                <button
                  onClick={handleAddRayon}
                  disabled={!newRayonName.trim()}
                  className="px-4 py-2 bg-[#F97316] text-white text-sm font-medium rounded-lg hover:bg-[#ea6c0a] disabled:opacity-50"
                >
                  Créer
                </button>
              </div>
            </motion.div>
          )}
          
          {/* Grille des rayons */}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
            {rayonsToUse.map((rayon) => {
              const style = RAYONS[rayon.id] || { emoji: '📦', color: '#6B7280' };
              return (
                <button
                  key={rayon.id}
                  onClick={() => {
                    setProductRayon(rayon.id);
                    setProductSousRayon('');
                    setShowAddSousRayon(false);
                  }}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    productRayon === rayon.id
                      ? 'border-[#F97316] bg-orange-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xl block">{style.emoji}</span>
                  <p className="text-xs mt-1 font-medium text-gray-700 line-clamp-2">{rayon.name}</p>
                  {rayon.sous_rayons?.length > 0 && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{rayon.sous_rayons.length} sous-rayons</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sous-rayon (affiché si rayon sélectionné) */}
        {selectedRayonData && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="overflow-hidden"
          >
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Sous-rayon * <span className="text-gray-400 font-normal">
                  ({selectedRayonData.sous_rayons?.length || 0} sous-rayons dans {selectedRayonData.name})
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowAddSousRayon(!showAddSousRayon)}
                className="text-xs text-[#1E3A5F] hover:underline flex items-center gap-1"
              >
                <Plus size={12} />
                Nouveau sous-rayon
              </button>
            </div>
            
            {/* Formulaire ajout sous-rayon */}
            {showAddSousRayon && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-3 p-3 bg-blue-50 rounded-xl border border-blue-200"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSousRayonName}
                    onChange={(e) => setNewSousRayonName(e.target.value)}
                    placeholder="Nom du nouveau sous-rayon"
                    className="flex-1 px-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                  />
                  <button
                    onClick={handleAddSousRayon}
                    disabled={!newSousRayonName.trim()}
                    className="px-4 py-2 bg-[#1E3A5F] text-white text-sm font-medium rounded-lg hover:bg-[#2d5a8a] disabled:opacity-50"
                  >
                    Ajouter
                  </button>
                </div>
              </motion.div>
            )}
            
            <div 
              className="p-3 rounded-xl border-2 border-dashed"
              style={{ borderColor: rayonStyle.color + '40', backgroundColor: rayonStyle.color + '08' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{rayonStyle.emoji}</span>
                <span className="font-medium text-gray-800">{selectedRayonData.name}</span>
                <ChevronRight size={16} className="text-gray-400" />
              </div>
              
              {selectedRayonData.sous_rayons?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {selectedRayonData.sous_rayons.map((sr) => (
                    <button
                      key={sr.id}
                      onClick={() => setProductSousRayon(sr.id)}
                      className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${
                        productSousRayon === sr.id
                          ? 'bg-white border-2 shadow-sm font-medium'
                          : 'bg-white/60 border border-gray-200 hover:bg-white hover:border-gray-300'
                      }`}
                      style={productSousRayon === sr.id ? { borderColor: rayonStyle.color } : {}}
                    >
                      {sr.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  Aucun sous-rayon pour ce rayon. 
                  <button 
                    onClick={() => setShowAddSousRayon(true)}
                    className="text-[#1E3A5F] hover:underline ml-1"
                  >
                    Créer le premier
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Prix et Stock */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prix TTC *</label>
            <div className="relative">
              <input
                type="number"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                placeholder="0.00"
                className="w-full px-4 py-2.5 pr-8 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
            <input
              type="number"
              value={productStock}
              onChange={(e) => setProductStock(e.target.value)}
              placeholder="0"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={productDescription}
            onChange={(e) => setProductDescription(e.target.value)}
            placeholder="Décris ton produit..."
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm resize-none"
          />
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Photos ({productImages.length}/5)</label>
          <div className="flex gap-2 flex-wrap">
            {productImages.map((img, index) => (
              <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden">
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(index, 'product')}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {productImages.length < 5 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-[#F97316] transition-colors"
              >
                <Plus size={24} className="text-gray-400" />
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleImageUpload(e, 'product')}
            className="hidden"
          />
        </div>
      </div>

      {/* Bouton publier */}
      <button
        onClick={handleSubmit}
        disabled={!productName.trim() || !productPrice || !productRayon || isLoading}
        className="mt-6 w-full py-3 bg-[#F97316] text-white font-semibold rounded-xl hover:bg-[#ea6c0a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Package size={18} />
            Publier sur la Marketplace
          </>
        )}
      </button>
    </div>
  )};
  

  // Formulaire Prestation
  const renderPrestationForm = () => (
    <div className="p-6 max-h-[70vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Nouvelle prestation</h2>
      <p className="text-gray-500 text-sm mb-4">Ce service sera visible sur l'annuaire Artisans</p>

      <div className="space-y-4">
        {/* Métier */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Métier *</label>
          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {METIERS.map((metier) => (
              <button
                key={metier.id}
                onClick={() => setPrestationMetier(metier.id)}
                className={`p-2 rounded-lg border-2 text-center transition-all ${
                  prestationMetier === metier.id
                    ? 'border-[#1E3A5F] bg-[#F0F4F8]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{metier.emoji}</span>
                <p className="text-xs mt-1 truncate">{metier.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Nom de la prestation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la prestation *</label>
          <input
            type="text"
            value={prestationName}
            onChange={(e) => setPrestationName(e.target.value)}
            placeholder="Ex: Installation et dépannage"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
          />
        </div>

        {/* Tarif et Zone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tarif horaire</label>
            <div className="relative">
              <input
                type="number"
                value={prestationPrice}
                onChange={(e) => setPrestationPrice(e.target.value)}
                placeholder="45"
                className="w-full px-4 py-2.5 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€/h</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zone d'intervention</label>
            <div className="relative">
              <input
                type="number"
                value={prestationZone}
                onChange={(e) => setPrestationZone(parseInt(e.target.value) || 30)}
                className="w-full px-4 py-2.5 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">km</span>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={prestationDescription}
            onChange={(e) => setPrestationDescription(e.target.value)}
            placeholder="Décris tes services, spécialités, certifications..."
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 text-sm resize-none"
          />
        </div>
      </div>

      {/* Bouton publier */}
      <button
        onClick={handleSubmit}
        disabled={!prestationMetier || !prestationName.trim() || isLoading}
        className="mt-6 w-full py-3 bg-[#1E3A5F] text-white font-semibold rounded-xl hover:bg-[#152a45] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Wrench size={18} />
            Activer cette prestation
          </>
        )}
      </button>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(1)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div>
                <h1 className="font-semibold text-white">Publier</h1>
                <p className="text-xs text-white/70">{userProfile?.name || 'Mon entreprise'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          {step === 1 ? renderStep1() : renderStep2()}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
